import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon, CheckCircle2 } from 'lucide-react';
import { Country, State, City } from 'country-state-city';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import LanguageMultiSelect from '../../components/common/LanguageMultiSelect';
import { getUserFacingError } from '../../services/errorMessage';
import { PREFERRED_LANGUAGE_OPTIONS } from '../../utils/languageOptions';

const formatRegisterError = (err) => {
  return getUserFacingError(err, 'Registration failed');
};

const Register = () => {
  const { theme, isDark, toggleTheme } = useTheme();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    area: '',
    countryCode: '',
    stateCode: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    whatsappOptIn: false,
    preferredLanguages: [],
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 900);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const countries = Country.getAllCountries();
  const states = formData.countryCode ? State.getStatesOfCountry(formData.countryCode) : [];
  const cities = formData.countryCode && formData.stateCode
    ? City.getCitiesOfState(formData.countryCode, formData.stateCode)
    : [];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCountryChange = (e) => {
    const nextCountryCode = e.target.value;
    const country = countries.find((c) => c.isoCode === nextCountryCode);
    setFormData((prev) => ({
      ...prev,
      countryCode: nextCountryCode,
      country: country?.name || '',
      stateCode: '',
      state: '',
      city: '',
    }));
  };

  const handleStateChange = (e) => {
    const nextStateCode = e.target.value;
    const state = states.find((s) => s.isoCode === nextStateCode);
    setFormData((prev) => ({
      ...prev,
      stateCode: nextStateCode,
      state: state?.name || '',
      city: '',
    }));
  };

  const handleCityChange = (e) => {
    const nextCity = e.target.value;
    setFormData((prev) => ({
      ...prev,
      city: nextCity,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        area: formData.area,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        postalCode: formData.postalCode,
        whatsappOptIn: formData.whatsappOptIn,
        preferredLanguages: formData.preferredLanguages,
        password: formData.password,
      });
      navigate('/user/meetings');
    } catch (err) {
      setError(formatRegisterError(err));
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      background: isDark
        ? 'radial-gradient(circle at 12% 12%, #1f2937 0%, transparent 35%), radial-gradient(circle at 88% 90%, #164e63 0%, transparent 35%), #0b1220'
        : 'radial-gradient(circle at 8% 20%, #fef9c3 0%, transparent 30%), radial-gradient(circle at 90% 85%, #dbeafe 0%, transparent 34%), #f8fafc',
      overflow: 'hidden',
    },
    themeToggle: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: theme.colors.backgroundSecondary,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: theme.colors.text,
      zIndex: 2,
      backdropFilter: 'blur(4px)',
    },
    wrapper: {
      width: '100%',
      maxWidth: '1180px',
      padding: isCompact ? '20px' : '24px',
      position: 'relative',
      zIndex: 1,
    },
    shell: {
      display: 'grid',
      gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(560px, 660px)',
      alignItems: 'stretch',
      columnGap: isCompact ? '18px' : '26px',
    },
    leftPane: {
      padding: isCompact ? '14px 10px' : '40px 18px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '18px',
    },
    rightPane: {
      padding: isCompact ? 0 : '8px 0',
      display: 'flex',
      alignItems: 'center',
    },
    leftEyebrow: {
      fontSize: '12px',
      fontWeight: '700',
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: theme.colors.primary,
    },
    leftTitle: {
      fontSize: isCompact ? '32px' : '44px',
      lineHeight: '1.1',
      fontWeight: '800',
      letterSpacing: '-0.03em',
      color: theme.colors.text,
      margin: 0,
    },
    leftDescription: {
      fontSize: '16px',
      lineHeight: '1.7',
      color: theme.colors.textSecondary,
      maxWidth: '460px',
      margin: 0,
    },
    featureList: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '10px',
      marginTop: '6px',
    },
    featureItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: theme.colors.textSecondary,
      fontSize: '14px',
      fontWeight: '500',
    },
    formColumn: {
      width: '100%',
    },
    authCard: {
      borderRadius: '16px',
      border: `1px solid ${theme.colors.border}`,
      boxShadow: '0 18px 32px rgba(15, 23, 42, 0.12)',
      backgroundColor: theme.colors.cardBg,
    },
    authCardBody: {
      padding: isCompact ? '20px' : '24px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: theme.colors.text,
      textAlign: 'left',
      marginBottom: '6px',
    },
    subtitle: {
      fontSize: '14px',
      color: theme.colors.textSecondary,
      textAlign: 'left',
      marginBottom: '20px',
    },
    error: {
      padding: '12px',
      borderRadius: '6px',
      backgroundColor: theme.colors.error + '20',
      color: theme.colors.error,
      fontSize: '14px',
      marginBottom: '16px',
    },
    footer: {
      marginTop: '16px',
      textAlign: 'center',
      fontSize: '14px',
      color: theme.colors.textSecondary,
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',
      columnGap: '14px',
    },
    gridSpanFull: {
      gridColumn: '1 / -1',
    },
    sectionTitle: {
      fontSize: '12px',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      color: theme.colors.textSecondary,
      marginBottom: '8px',
      marginTop: '2px',
      gridColumn: '1 / -1',
      fontWeight: '700',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      marginBottom: '14px',
      color: theme.colors.textSecondary,
      fontSize: '13px',
    },
    link: {
      color: theme.colors.primary,
      cursor: 'pointer',
      fontWeight: '500',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
    },
    selectField: {
      marginBottom: '20px',
      width: '100%',
    },
    selectLabel: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    selectInput: {
      width: '100%',
      padding: '11px 14px',
      fontSize: '14px',
      borderRadius: '8px',
      border: `2px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.inputBg,
      color: theme.colors.text,
      outline: 'none',
      fontFamily: 'inherit',
      colorScheme: theme.name === 'dark' ? 'dark' : 'light',
    },
    selectHelp: {
      marginTop: '6px',
      fontSize: '13px',
      color: theme.colors.textSecondary,
    },
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.themeToggle}
        onClick={toggleTheme}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary;
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary;
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div style={styles.wrapper}>
        <div style={styles.shell}>
          <div style={styles.leftPane}>
            <div style={styles.leftEyebrow}>Create Access</div>
            <h1 style={styles.leftTitle}>Join the Health Conference Platform</h1>
            <p style={styles.leftDescription}>
              Get your attendee account in one quick flow and start joining sessions instantly.
            </p>

            <div style={styles.featureList}>
              <div style={styles.featureItem}><CheckCircle2 size={16} /> Fast attendee onboarding</div>
              <div style={styles.featureItem}><CheckCircle2 size={16} /> Personalized session access</div>
              <div style={styles.featureItem}><CheckCircle2 size={16} /> Secure meeting participation</div>
            </div>
          </div>

          <div style={styles.rightPane}>
            <div style={styles.formColumn}>
              <h1 style={styles.title}>Create Account</h1>
              <p style={styles.subtitle}>A compact form, same required details.</p>

              <Card cardStyle={styles.authCard} bodyStyle={styles.authCardBody}>
                <form onSubmit={handleSubmit}>
                  {error && <div style={styles.error}>{error}</div>}

                  <div style={styles.formGrid}>
                    <div style={styles.sectionTitle}>Basic details</div>

                    <Input
                      type="text"
                      name="name"
                      label="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                    />

                    <Input
                      type="email"
                      name="email"
                      label="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />

                    <div style={styles.gridSpanFull}>
                      <Input
                        type="tel"
                        name="phone"
                        label="Mobile Number"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your mobile number"
                        required
                        helperText="Use the number you plan to use for future WhatsApp OTP sign-in."
                      />
                    </div>

                    <div style={styles.sectionTitle}>Location details</div>

                    <Input
                      type="text"
                      name="area"
                      label="Area / Locality"
                      value={formData.area}
                      onChange={handleChange}
                      placeholder="Enter your area or locality"
                      required
                    />

                    <div style={styles.selectField}>
                      <label style={styles.selectLabel}>
                        Country <span style={{ color: theme.colors.error }}> *</span>
                      </label>
                      <select
                        value={formData.countryCode}
                        onChange={handleCountryChange}
                        required
                        style={styles.selectInput}
                      >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.selectField}>
                      <label style={styles.selectLabel}>
                        State <span style={{ color: theme.colors.error }}> *</span>
                      </label>
                      <select
                        value={formData.stateCode}
                        onChange={handleStateChange}
                        required
                        disabled={!formData.countryCode}
                        style={styles.selectInput}
                      >
                        <option value="">Select state</option>
                        {states.map((state) => (
                          <option key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={styles.selectField}>
                      <label style={styles.selectLabel}>
                        City <span style={{ color: theme.colors.error }}> *</span>
                      </label>
                      <select
                        value={formData.city}
                        onChange={handleCityChange}
                        required
                        disabled={!formData.stateCode}
                        style={styles.selectInput}
                      >
                        <option value="">Select city</option>
                        {cities.map((city) => (
                          <option key={`${city.name}-${city.latitude}-${city.longitude}`} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                      <div style={styles.selectHelp}>Pick location values from the verified list.</div>
                    </div>

                    <div style={styles.gridSpanFull}>
                      <Input
                        type="text"
                        name="postalCode"
                        label="Postal Code"
                        value={formData.postalCode}
                        onChange={handleChange}
                        placeholder="Enter your postal code"
                      />
                    </div>

                    <div style={styles.gridSpanFull}>
                      <LanguageMultiSelect
                        label="Preferred Language(s)"
                        options={PREFERRED_LANGUAGE_OPTIONS}
                        value={formData.preferredLanguages}
                        onChange={(nextValue) => setFormData((prev) => ({ ...prev, preferredLanguages: nextValue }))}
                        helperText="Select all languages you prefer for meeting recommendations."
                      />
                    </div>

                    <div style={styles.sectionTitle}>Security</div>

                    <Input
                      type="password"
                      name="password"
                      label="Password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      required
                    />

                    <Input
                      type="password"
                      name="confirmPassword"
                      label="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      required
                    />
                  </div>

                  <label style={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      name="whatsappOptIn"
                      checked={formData.whatsappOptIn}
                      onChange={handleChange}
                    />
                    <span>
                      I agree to receive meeting updates and future location-based notifications on WhatsApp.
                    </span>
                  </label>

                  <Button type="submit" fullWidth disabled={loading}>
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>

                <div style={styles.footer}>
                  Already have an account?{' '}
                  <span style={styles.link} onClick={() => navigate('/login')}>
                    Sign in
                  </span>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
